import { EmpathiseColour, EngineId, TopicAnalysis } from '../../../../src/commonTypes';
import { removeChildren } from './uiUtils';
import { resizeLongSuggestionsToFit } from './toolsUI';
import {
	clickedEmpathiseResults,
	empathiseResultsGenerated,
	lateralSliderMoved,
	TUTORIAL_EVENT_TYPES,
	tutorialEmitter,
} from '../tutorials/tutorialEvents';
import { ENGINE_TYPES, PHRASE_TOOL_UI } from '../../../../src/commonConstants';
import * as ncApi from '../apiRequests';
import { clearFlowyHighlight, highlightFlowyTarget } from './flowyHighlight';
import { toggleToolContainer } from './toolsUI';
import { InspireGraph } from '../graphs';

const ENGINE_FILTERS: Record<EngineId, Array<{ key: string; label: string }>> = {
	Creative: [
		{ key: 'Filter0', label: 'None' },
		{ key: 'Filter1', label: 'Advertising' },
		{ key: 'Filter2', label: 'Brand' },
		{ key: 'Filter3', label: 'Media' },
	],
	Games: [
		{ key: 'Filter0', label: 'None' },
		{ key: 'Filter1', label: 'Mechanics' },
		{ key: 'Filter2', label: 'Interaction' },
	],
	Innovative: [
		{ key: 'Filter0', label: 'Product Design' },
		{ key: 'Filter1', label: 'Values' },
		{ key: 'Filter2', label: 'Customer Strategy' },
		{ key: 'Filter3', label: 'Innovation' },
		{ key: 'Filter4', label: 'Ethics' },
	],
	Create: [
		{ key: 'Filter0', label: 'All perspectives' },
		{ key: 'Filter1', label: 'Positive' },
		{ key: 'Filter2', label: 'Challenges & risks' },
		{ key: 'Filter3', label: 'Emotional' },
		{ key: 'Filter4', label: 'Co-occurrences' },
	],
};

type EmpathiseLane = { color: EmpathiseColour; values: TopicAnalysis[] };

type EmpathiseDisplayContext = {
	boardId: string;
	seedLabel: string;
	contextPath: string[];
	rawLanes: EmpathiseLane[];
	enrichedLanes: EmpathiseLane[];
	visiblePerLane: number;
	isFromTopic: boolean;
	addSingleNode: (word: TopicAnalysis, colour: EmpathiseColour) => void;
};

let activeEmpathiseDisplay: EmpathiseDisplayContext | null = null;
let empathiseEnrichRequestId = 0;

const phraseMatchKey = (phrase: TopicAnalysis) => phrase.rawKey || phrase.key;

const getVisiblePerLane = (sliderValue: number): number =>
	sliderValue >= PHRASE_TOOL_UI.EMPATHISE_SECOND_PHRASE_SLIDER_THRESHOLD ? 2 : 1;

const getVisibleEmpathiseLanes = (
	lanes: EmpathiseLane[],
	visiblePerLane: number
): EmpathiseLane[] =>
	lanes.map((lane) => ({
		color: lane.color,
		values: lane.values.slice(0, visiblePerLane),
	}));

const removePhraseFromLane = (
	lanes: EmpathiseLane[],
	colour: EmpathiseColour,
	phrase: TopicAnalysis
): EmpathiseLane[] => {
	const key = phraseMatchKey(phrase);
	return lanes.map((lane) => {
		if (lane.color !== colour) {
			return lane;
		}
		return {
			color: lane.color,
			values: lane.values.filter((item) => phraseMatchKey(item) !== key),
		};
	});
};

export const setupEmpathiseResults = (
	boardId: string,
	seedLabel: string,
	addSingleNode: (word: TopicAnalysis, colour: EmpathiseColour) => void,
	results: EmpathiseLane[],
	isFromTopic: boolean,
	contextPath: string[] = []
) => {
	const container = document.querySelector('#empathise-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const lateralThinkingSlider = container.querySelector<HTMLInputElement>("input[type='range']")!;

	loadingIndicator.classList.add('hidden');

	const sliderValue = parseInt(lateralThinkingSlider.value, 10);
	activeEmpathiseDisplay = {
		boardId,
		seedLabel,
		contextPath,
		rawLanes: results,
		enrichedLanes: [],
		visiblePerLane: getVisiblePerLane(sliderValue),
		isFromTopic,
		addSingleNode,
	};

	void refreshEmpathiseDisplay(sliderValue);

	lateralThinkingSlider.oninput = () => {
		lateralSliderMoved(parseInt(lateralThinkingSlider.value, 10));
		tutorialEmitter.emit(
			TUTORIAL_EVENT_TYPES.lateralSliderMoved,
			parseInt(lateralThinkingSlider.value, 10)
		);
	};

	lateralThinkingSlider.onchange = () => {
		void refreshEmpathiseDisplay(parseInt(lateralThinkingSlider.value, 10));
	};
};

const renderEmpathiseLoading = () => {
	const pairsList = document.querySelector('#empathise-container .pairs-list')!;
	pairsList.innerHTML = '<p class="phrase-loading"><em>Preparing phrases…</em></p>';
};

const renderEmpathisePhrases = (
	lanes: EmpathiseLane[],
	isFromTopic: boolean,
	emitResultsEvent: boolean
) => {
	const context = activeEmpathiseDisplay;
	if (!context) {
		return;
	}

	const pairsList = document.querySelector('#empathise-container .pairs-list')!;
	const phrasesToShow: Array<{ color: EmpathiseColour; values: TopicAnalysis }> = [];
	removeChildren(pairsList);
	const notFromTopic = document.querySelector('#empathise-container #not-from-topic')!;

	if (!lanes.some(({ values }) => values.length)) {
		pairsList.innerHTML = '<p><em>No results</em></p>';
	}

	if (!isFromTopic) {
		notFromTopic.innerHTML = 'There is no result from current selected PDF.';
	} else {
		notFromTopic.innerHTML = '';
	}

	let resultIndex = 0;
	for (const lane of lanes) {
		for (const phrase of lane.values) {
			pairsList.append(getWordLi(lane.color, phrase, resultIndex));
			phrasesToShow.push({ color: lane.color, values: phrase });
			resultIndex += 1;
		}
	}

	if (emitResultsEvent) {
		empathiseResultsGenerated(
			phrasesToShow.map((entry) => ({
				color: entry.color,
				values: entry.values.key,
			}))
		);
	}
	resizeLongSuggestionsToFit();
};

const renderVisibleEmpathiseFromContext = (emitResultsEvent: boolean) => {
	const context = activeEmpathiseDisplay;
	if (!context) {
		return;
	}
	renderEmpathisePhrases(
		getVisibleEmpathiseLanes(context.enrichedLanes, context.visiblePerLane),
		context.isFromTopic,
		emitResultsEvent
	);
};

const refreshEmpathiseDisplay = async (sliderValue: number) => {
	const context = activeEmpathiseDisplay;
	if (!context) {
		return;
	}

	context.visiblePerLane = getVisiblePerLane(sliderValue);
	const requestId = ++empathiseEnrichRequestId;
	renderEmpathiseLoading();

	try {
		const enriched = await ncApi.enrichEmpathisePhrases(context.boardId, {
			seedLabel: context.seedLabel,
			contextPath: context.contextPath,
			sliderValue,
			lanes: context.rawLanes,
		});
		if (requestId !== empathiseEnrichRequestId) {
			return;
		}
		context.enrichedLanes = enriched;
		renderVisibleEmpathiseFromContext(true);
	} catch {
		if (requestId !== empathiseEnrichRequestId) {
			return;
		}
		context.enrichedLanes = context.rawLanes;
		renderVisibleEmpathiseFromContext(true);
	}
};

const getWordLi = (colour: EmpathiseColour, word: TopicAnalysis, resultIndex: number) => {
	const wordLi = document.createElement('li');
	wordLi.textContent = word.key;
	wordLi.className = colour;
	wordLi.dataset.word = word.key;
	wordLi.dataset.resultId = `${colour}:${phraseMatchKey(word)}`;
	wordLi.dataset.resultIndex = String(resultIndex);
	wordLi.dataset.resultTitle = word.key;
	wordLi.dataset.resultSnippet = word.key;
	wordLi.onclick = () => {
		const context = activeEmpathiseDisplay;
		if (!context) {
			return;
		}

		clickedEmpathiseResults(word.key);
		context.addSingleNode(word, colour);

		const rawLane = context.rawLanes.find((lane) => lane.color === colour);
		if (rawLane) {
			const key = phraseMatchKey(word);
			const rawIndex = rawLane.values.findIndex((item) => phraseMatchKey(item) === key);
			if (rawIndex >= 0) {
				rawLane.values.splice(rawIndex, 1);
			}
		}

		context.enrichedLanes = removePhraseFromLane(context.enrichedLanes, colour, word);
		renderVisibleEmpathiseFromContext(false);
	};
	return wordLi;
};

type BackendEmpathiseResult = {
	id?: string;
	title?: string;
	label?: string;
	snippet?: string;
	colour?: string;
	color?: string;
	topics?: string[];
};

const isEmpathiseColour = (value: string): value is EmpathiseColour =>
	[
		'FOREST',
		'RED',
		'FUSCHIA',
		'BLUE',
		'TAN',
		'SALMON',
		'GREENBLUE',
		'GREEN',
		'LINK',
		'IDEATE',
	].includes(value);

export const highlightEmpathiseResult = ({
	resultId,
	resultIds,
	resultIndex,
	label,
}: {
	resultId?: string | null;
	resultIds?: string[] | null;
	resultIndex?: number | null;
	label?: string | null;
}) => {
	const container = document.querySelector('#empathise-container');
	if (!container) return false;
	const normalizedLabel = (label || '').trim().toLowerCase();
	const resultIdSet = new Set([...(resultId ? [resultId] : []), ...(resultIds || [])]);
	let matched = false;
	container.querySelectorAll<HTMLLIElement>('.pairs-list li').forEach((item) => {
		const title = (item.dataset.resultTitle || '').trim().toLowerCase();
		const snippet = (item.dataset.resultSnippet || '').trim().toLowerCase();
		const idMatches = Boolean(item.dataset.resultId && resultIdSet.has(item.dataset.resultId));
		const indexMatches = Boolean(
			typeof resultIndex === 'number' && item.dataset.resultIndex === String(resultIndex)
		);
		const labelMatches = Boolean(
			normalizedLabel &&
				(title === normalizedLabel ||
					snippet === normalizedLabel ||
					title.includes(normalizedLabel) ||
					snippet.includes(normalizedLabel) ||
					normalizedLabel.includes(title) ||
					normalizedLabel.includes(snippet))
		);
		const isMatch = indexMatches || idMatches || labelMatches;
		item.classList.toggle('flowy-recommended-result', isMatch);
		if (isMatch && !matched) {
			matched = true;
			highlightFlowyTarget({
				target: item,
				label: 'Flowy pick',
			});
		}
	});
	return matched;
};

const resolveBackendEmpathiseNode = ({
	selectedNodeIds,
	selectedNodeLabels,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
}) => {
	const graph = globalThis.neuroCreate.graph;
	const selectedId = selectedNodeIds?.[0];
	const existingById = selectedId ? graph?.nodes.find((node) => node.uid === selectedId) : null;
	if (existingById) return existingById;
	const selectedLabel = selectedNodeLabels?.find((label) => label.trim());
	const existingByLabel = selectedLabel
		? graph?.nodes.find((node) => (node.label || '').trim() === selectedLabel.trim())
		: null;
	return existingByLabel || graph?.activeNode || null;
};

export const openBackendEmpathisePanel = ({
	selectedNodeIds,
	selectedNodeLabels,
}: {
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
}) => {
	const node = resolveBackendEmpathiseNode({ selectedNodeIds, selectedNodeLabels });
	if (node) {
		toggleToolContainer('empathise', node);
	}
	document.querySelector('#empathise-container')?.classList.remove('hidden');
	resetEmpathise(async () => undefined);
};

export const showBackendEmpathiseResults = ({
	results,
	selectedNodeIds,
	selectedNodeLabels,
	recommendedResultId,
	recommendedResultIds,
}: {
	results: BackendEmpathiseResult[];
	selectedNodeIds?: string[];
	selectedNodeLabels?: string[];
	recommendedResultId?: string | null;
	recommendedResultIds?: string[];
}) => {
	const container = document.querySelector('#empathise-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const pairsList = container.querySelector('.pairs-list')!;
	const notFromTopic = container.querySelector('#not-from-topic')!;
	const seedNode = resolveBackendEmpathiseNode({ selectedNodeIds, selectedNodeLabels });
	loadingIndicator.classList.add('hidden');
	notFromTopic.innerHTML = '';
	removeChildren(pairsList);
	clearFlowyHighlight();
	const recommendedIds = new Set([
		...(recommendedResultId ? [recommendedResultId] : []),
		...(recommendedResultIds || []),
	]);

	results.forEach((entry, index) => {
		const label = entry.label || entry.title || entry.snippet || '';
		if (!label) return;
		const rawColour = entry.colour || entry.color || '';
		const colour: EmpathiseColour = isEmpathiseColour(rawColour) ? rawColour : 'FUSCHIA';
		const resultId = entry.id || `${colour}:${label}`;
		const itemEl = document.createElement('li');
		itemEl.textContent = label;
		itemEl.className = colour;
		itemEl.dataset.word = label;
		itemEl.dataset.resultId = resultId;
		itemEl.dataset.resultIndex = String(index);
		itemEl.dataset.resultTitle = label;
		itemEl.dataset.resultSnippet = entry.snippet || label;
		if (recommendedIds.has(resultId)) {
			itemEl.classList.add('flowy-recommended-result');
		}
		itemEl.onclick = () => {
			const graph = globalThis.neuroCreate.graph as InspireGraph | undefined;
			if (graph && seedNode) {
				graph.addSingleNode(seedNode, label, colour, entry.topics || []);
				clickedEmpathiseResults(label);
				itemEl.remove();
				clearFlowyHighlight();
			}
		};
		pairsList.append(itemEl);
	});

	const resultIdsToHighlight = [...recommendedIds];
	if (resultIdsToHighlight.length) {
		window.setTimeout(() => {
			highlightEmpathiseResult({ resultIds: resultIdsToHighlight });
		}, 80);
	}
	resizeLongSuggestionsToFit();
};

export const resetEmpathise = (fetchEmpathiseResults: () => Promise<void>) => {
	const container = document.querySelector('#empathise-container')!;
	const loadingIndicator = container.querySelector('.loading')!;
	const pairsList = container.querySelector('.pairs-list')!;
	const filterSelect = container.querySelector<HTMLSelectElement>('.filter')!;

	removeChildren(pairsList);
	removeChildren(filterSelect);
	clearFlowyHighlight();
	activeEmpathiseDisplay = null;

	let { engine } = document.body.dataset;

	const filterOptions = ENGINE_FILTERS[engine as EngineId];
	if (!filterOptions) {
		alert('Error: unknown engine type used for this board. Please contact us');
	}
	filterSelect.append(
		...filterOptions.map((filter) => {
			const selectOption = document.createElement('option');
			selectOption.value = filter.key;
			selectOption.innerText = filter.label;
			return selectOption;
		})
	);
	filterSelect.onchange = () => {
		void fetchEmpathiseResults();
	};

	loadingIndicator.classList.remove('hidden');
};
